import { t as __t } from "i18next";
import { QRCodeCanvas } from "qrcode.react";
import React, { useEffect, useMemo, useState } from "react";
import { convertStringToJson, displayUrl } from "../../../utils/commonUtil";
import { APPROVE, PAYMENT } from "../../../utils/commonType";
import PermissionWrapper from "../../../components/common/PermissionWrapper";
import { openDocumentPreview } from "../../../components/document-preview";

function isShown(v) {
    // ໂຊວເມື່ອມີຄ່າ ແລະ ບໍ່ແມ່ນ 0
    return v !== null && v !== undefined && Number(v) !== 0;
}

export default function PaymentPage({
    qrCode,
    t,
    FormatCurrencys,
    onPay,
    isDebit = false,
    countdownSeconds = 60, // ຮັບຄ່າວິນາທີຈາກ parent ເຊັ່ນ 60, 59, 58...
}) {
    const currency = t("currentcy.kip");
    const [countdown, setCountdown] = useState(countdownSeconds);

    useEffect(() => {
        if (!qrCode?.value) {
            setCountdown(countdownSeconds);
            return;
        }

        setCountdown(countdownSeconds);

        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }

                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [qrCode?.value, countdownSeconds]);
    const formatCountdown = (seconds) => {
        const sec = Number(seconds || 0);
        const m = Math.floor(sec / 60);
        const s = sec % 60;

        return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    };

    const items = useMemo(() => {
        if (!qrCode) return [];

        const config = [
            { key: "payPerMonth", label: t("contracts.vehicle.payPerMonth"), type: "add" },
            { key: "vipAmount", label: t("contracts.vehicle.vipAmount"), type: "add" },
            { key: "vipAmount0", label: t("contracts.vehicle.vipAmount"), type: "add" },
            { key: "vipAmount1", label: t("contracts.vehicle.vipAmount"), type: "add" },
            { key: "discount", label: t("contracts.vehicle.discount"), type: "minus" },
            { key: "priceAfterDiscount", label: t("contracts.vehicle.priceAfterDiscount"), type: "neutral" },
            { key: "civelLiability", label: t("contracts.vehicle.civelLiability"), type: "add" },
            { key: "guaranteenFee", label: t("contracts.vehicle.guaranteenFee"), type: "add" },
            { key: "guaranteenFee0", label: t("contracts.vehicle.guaranteenFee"), type: "add" },
            { key: "guaranteenFee1", label: t("contracts.vehicle.guaranteenFee"), type: "add" },
            { key: "guaranteenFee2", label: t("contracts.vehicle.guaranteenFee"), type: "add" },
            { key: "sumTotal", label: t("contracts.vehicle.sumTotal"), type: "neutral" },
            { key: "repairCost", label: t("contracts.vehicle.repairCost"), type: "add" },
            { key: "vatValue", label: t("contracts.vehicle.vatValue"), type: "add" },
            { key: "RVPEAmount", label: t("contracts.vehicle.RVPEAmount"), type: "add" },
            { key: "feeRegister", label: t("contracts.vehicle.feeRegister"), type: "add" },
            { key: "total", label: t("contracts.vehicle.total"), type: "neutral" },
            {
                key: "commission",
                label: t("contracts.vehicle.commission"),
                type: "minus",
                percent: "commissionRate",
                percent3D: "comrate3D",
            },
            { key: "paymentFee", label: t("contracts.vehicle.paymentFee"), type: "add" },
            {
                key: "companyReceiveTotal",
                label: t("contracts.vehicle.companyReceiveTotal"),
                type: "neutral",
                isTotal: true,
            },
        ];

        return config
            .map((c) => ({
                ...c,
                value: qrCode?.[c.key],
                percent: c.percent && c.percent3D ? `(${Number(qrCode?.[c.percent]) - (Number(qrCode?.[c.percent3D]) || 0)} + ${Number(qrCode?.[c.percent3D]) || 0}%)` : c.percent ? `(${qrCode?.[c.percent] || 0}%)` : "",
            }))
            .filter((x) => isShown(x.value));
    }, [qrCode, t]);

    const totalPayable = useMemo(() => {
        return Number(qrCode?.companyReceiveTotal || qrCode?.total || 0);
    }, [qrCode]);

    const openFile = (e, value) => {
        e.preventDefault();

        if (value) openDocumentPreview(value);

    };

    const renderValue = (item) => {
        const n = Number(item.value || 0);
        const text = `${FormatCurrencys(Math.abs(n))} ${currency}`;

        if (item.type === "minus") {
            return <span className="payment-minus">-{text}</span>;
        }

        if (item.isTotal) {
            return <span className="payment-total-value">{text}</span>;
        }

        if (item.type === "add") {
            return <span className="payment-add">{text}</span>;
        }

        return <span className="payment-normal">{text}</span>;
    };

    return (
        <div className="payment-modern-wrapper">
            <div className="payment-modern-header">
                <div>
                    <div className="payment-mini-label">
                        {t("payment.title") || "Payment"}
                    </div>

                    <h4>{t("payment.checkout") || "Checkout"}</h4>

                    <small>
                        {t("payment.ref") || "Reference"}:{" "}
                        {qrCode?.transactionId || "-"}
                    </small>
                </div>

                <div className="payment-status-box">
                    <div className="payment-mini-label">
                        {t("payment.status") || "Status"}
                    </div>

                    {qrCode?.status == 1 ? (
                        <span className="payment-badge-success">
                            {t("payment.success") || "Success"}
                        </span>
                    ) : (
                        <span className="payment-badge-warning">
                            {t("payment.warning") || "Pending"}
                        </span>
                    )}
                </div>
            </div>

            <div className={qrCode?.status ? "payment-main-grid single" : "payment-main-grid"}>
                {/* LEFT */}
                <div className="payment-card">
                    <div className="payment-card-title">
                        <h5>{t("payment.breakdown") || "Payment breakdown"}</h5>
                        <span>{t("payment.currency") || "Currency"}</span>
                    </div>

                    <div className="payment-breakdown-list">
                        {items.map((item) => (
                            <div className="payment-breakdown-item" key={item.key}>
                                <div className="payment-label">
                                    {item.label}

                                    {item.percent && (
                                        <span className="payment-percent">
                                            {item.percent}
                                        </span>
                                    )}
                                </div>

                                <div className="payment-value">
                                    {renderValue(item)}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="payment-total-card">
                        <div>
                            <div className="payment-mini-label">
                                {t("payment.totalPayable") || "Total payable"}
                            </div>

                            <div className="payment-grand-total">
                                {FormatCurrencys(totalPayable)} {currency}
                            </div>
                        </div>

                        <div>
                            {qrCode?.status == 1 ? (
                                <span className="payment-badge-success">
                                    {t("payment.success") || "Success"}
                                </span>
                            ) : (
                                <span className="payment-badge-warning">
                                    {t("payment.warning") || "Pending"}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="payment-action-row">
                        <PermissionWrapper permission={APPROVE}>
                            <button
                                disabled={qrCode?.status == 1 || !qrCode?.status}
                                className="payment-btn payment-btn-danger"
                                onClick={() => onPay?.(qrCode)}
                            >
                                {t("payments.payNow") || "Pay now"}
                            </button>
                        </PermissionWrapper>

                        <button
                            disabled={!qrCode?.billPath}
                            className="payment-btn payment-btn-outline"
                            onClick={(e) =>
                                openFile(
                                    e,
                                    convertStringToJson(qrCode?.billPath)?.[0]
                                )
                            }
                        >
                            {t("payments.downloadInvoice") || "Download invoice"}
                        </button>
                    </div>
                </div>

                {/* RIGHT */}
                {!qrCode?.status && (
                    <div className="payment-card">
                        <div className="payment-card-title">
                            <h5>{t("payment.method") || "Payment method"}</h5>
                        </div>

                        <div className="payment-method-text">
                            {t("payment.chooseMethod") ||
                                "Choose a method to complete payment."}
                        </div>

                        <div className="payment-method-list">
                            <button
                                className="payment-method-card"
                                onClick={() => onPay?.({ inDebt: false })}
                            >
                                <div className="payment-method-icon">🏦</div>

                                <div>
                                    <div className="payment-method-title">
                                        {t("payment.bankTransfer") || "Bank transfer"}
                                    </div>

                                    <div className="payment-method-desc">
                                        {t("payment.bcelone") ||
                                            "Pay via bank app / transfer slip"}
                                    </div>
                                </div>
                            </button>
                            <PermissionWrapper permission={PAYMENT}>
                                <button
                                    disabled={isDebit}
                                    className="payment-method-card"
                                    onClick={() => onPay?.({ inFree: 1 })}
                                >
                                    <div className="payment-method-icon">💵</div>

                                    <div>
                                        <div className="payment-method-title">
                                            {t("payment.free") || "Cash"}
                                        </div>

                                        <div className="payment-method-desc">
                                            {t("payment.staffFree") ||
                                                "Pay at staff"}
                                        </div>
                                    </div>
                                </button>
                            </PermissionWrapper>
                            <button
                                disabled={isDebit}
                                className="payment-method-card"
                                onClick={() => onPay?.({ inDebt: true })}
                            >
                                <div className="payment-method-icon">💵</div>

                                <div>
                                    <div className="payment-method-title">
                                        {t("payment.debit") || "Cash"}
                                    </div>

                                    <div className="payment-method-desc">
                                        {t("payment.debitCounter") ||
                                            "Pay at counter / agent"}
                                    </div>
                                </div>
                            </button>
                        </div>

                        <div className="payment-qr-box-v">
                            <div className="payment-mini-label mb-2">
                                {t("payment.qrPlaceholder") || "QR will show here"}
                            </div>

                            <div className="payment-qr-frame">
                                {qrCode?.value ? (
                                    <div className="payment-qr-inner">
                                        <QRCodeCanvas
                                            value={qrCode.value}
                                            size={260}
                                            bgColor="#ffffff"
                                            fgColor="#000000"
                                            level="H"
                                            includeMargin={true}
                                        />

                                        <img
                                            src="/images/lti_logo.png"
                                            alt="logo"
                                            className="payment-qr-logo"
                                        />
                                    </div>
                                ) : (
                                    <span className="text-muted">{__t("uiText.text426")}</span>
                                )}
                            </div>

                            {countdown !== null && countdown !== undefined && qrCode?.value && (
                                <div className="payment-countdown-box">
                                    <div className="payment-countdown-label">
                                        {t("payment.expireIn") || "QR expires in"}
                                    </div>

                                    <div className="payment-countdown-time">
                                        {formatCountdown(countdown)}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}